/** biome-ignore-all lint/suspicious/noExplicitAny: Input can have any value since it's parsed using zod */
import { zodResolver } from "@hookform/resolvers/zod"
import { useState } from "react"
import type { FieldValues, UseFormProps } from "react-hook-form"
import { useForm } from "react-hook-form"
import type { z } from "zod/v4"

import { formLogger } from "@/lib/logger"

const unexpectedErrorMessage =
  "Erro inesperado, tente novamente em alguns minutos."

export type TActionResponse =
  | Promise<void>
  | Promise<{ success: boolean; message: string | null; params?: unknown }>

type TFormState = {
  errorMessage: string | null
  successMessage: string | null
  isFormSubmitted: boolean
  isSubmitting: boolean
  hasSucceeded: boolean
}

interface IFormState<TFieldValues extends FieldValues = FieldValues>
  extends ReturnType<typeof useForm<TFieldValues>> {
  onSubmit: (data: TFieldValues) => Promise<void> | void
  state: TFormState
}

interface IUseFormStateProps<TSchema extends z.ZodObject<any>>
  extends UseFormProps<z.output<TSchema>> {
  zodSchema: TSchema
  action: (data: z.output<TSchema>) => TActionResponse
  onSuccess?: (
    values: z.output<TSchema>,
    responseParams?: any,
  ) => Promise<void> | void
  onFailure?: (
    values: z.output<TSchema>,
    error?: string,
  ) => Promise<void> | void
}

export function useFormState<TSchema extends z.ZodObject<any>>(
  props: IUseFormStateProps<TSchema>,
): IFormState<z.output<TSchema>> {
  const { action, onSuccess, onFailure, ...useFormProps } = props
  const [message, setMessage] = useState<string | null>(null)
  const [success, setSuccess] = useState<boolean>(false)

  // Initialize react-hook-form
  const formMethods = useForm<z.output<TSchema>>({
    ...useFormProps,
    resolver: zodResolver(props.zodSchema) as any,
  })

  /**
   * Parse response and extract success status, message, and parameters
   */
  const parseActionResponse = (response: any) => {
    const result = {
      hasSucceeded: true,
      message: null as string | null,
      params: undefined as any,
    }

    if (typeof response === "object" && response !== null) {
      // Extract success status if available
      if ("success" in response) {
        result.hasSucceeded = response.success
      }

      // Extract message if available
      if ("message" in response && response.message) {
        result.message = response.message
      }

      // Extract params if available
      if ("params" in response) {
        result.params = response.params
      }
    }

    return result
  }

  /**
   * Handle successful form submission
   */
  const handleSuccess = async (
    data: z.output<TSchema>,
    responseMessage: string | null,
    params?: any,
  ) => {
    if (onSuccess) {
      await onSuccess(data, params)
    }
    setMessage(responseMessage)
    setSuccess(true)
  }

  /**
   * Handle failed form submission
   */
  const handleFailure = async (
    data: z.output<TSchema>,
    errorMessage: string | null,
  ) => {
    const finalMessage = errorMessage ?? unexpectedErrorMessage
    setMessage(finalMessage)
    setSuccess(false)

    if (onFailure) {
      await onFailure(data, finalMessage)
    }
  }

  // Define the submit handler with reduced complexity
  const onSubmit = async (data: z.output<TSchema>) => {
    setMessage(null)
    setSuccess(false)

    try {
      let dataContent = data
      if (Object.keys(data).length === 0) {
        dataContent = props.zodSchema.parse(formMethods.getValues())
      }
      const response = await action(dataContent)
      const { hasSucceeded, message, params } = parseActionResponse(response)

      if (hasSucceeded) {
        await handleSuccess(data, message, params)
      } else {
        await handleFailure(data, message)
      }
    } catch (error) {
      let errorMessage = unexpectedErrorMessage

      if (error instanceof Error) {
        // Use centralized form error logging
        formLogger.formError("Form submission failed", error)
        errorMessage = error.message || errorMessage
      } else {
        // Use centralized form error logging for unknown errors
        formLogger.formError("Unknown form submission error", error)
      }

      await handleFailure(data, errorMessage)
    }
  }

  const formState = formMethods.formState

  return {
    ...formMethods,
    state: {
      errorMessage: formState.isSubmitted && !success ? message : null,
      successMessage: formState.isSubmitted && success ? message : null,
      isFormSubmitted: formState.isSubmitted,
      isSubmitting: formState.isSubmitting,
      hasSucceeded: formState.isSubmitted && success,
    },
    onSubmit,
  } as IFormState<z.output<TSchema>>
}
